import React, { type ReactNode, useEffect, useState } from 'react';
import { ModeToggle } from '../ThemeMode/mode-toggle.tsx';
import { TendrilLogoColorTyped } from '@assets/index.ts';
import { getCurrentUser } from '@features/auth';
import { Separator } from '@core-ui/Components/ui/separator.tsx';
import UserDropdown from '../MainLayout/UserDropdown.tsx';
import LanguageToggle from '@ui/shared/LanguageToggle.tsx';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '@features/auth/store/useSessionStore.ts';
import { useTwilioVoice } from '@hooks/useTwilioVoice.ts';
import { ENV } from '@constants/env.ts';
import { EnvVersionBanner } from '@ui/shared/envVersionBanner.tsx';
import MicrophoneSetupModal from '@ui/shared/Dialer/MicrophoneSetupModal.tsx';
import { useFeatureFlagsStore } from '@store/featureFlags';
import VersionCheckBanner from '@ui/shared/VersionCheckBanner.tsx';
import { useVersionCheck } from '@hooks/useVersionCheck.ts';

interface AgentLayoutProps {
  children: ReactNode;
}

const AgentLayout: React.FC<AgentLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const currentUser = getCurrentUser();
  const { hasNewVersion, newVersion, currentVersion } = useVersionCheck();
  const loadFeatureFlags = useFeatureFlagsStore((state) => state.loadFeatureFlags);
  const checkSessionValidity = useSessionStore(
    (state) => state.checkSessionValidity,
  );
  const identity = currentUser?.userId;

  useTwilioVoice(identity);

  useEffect(() => {
    const cleanup = checkSessionValidity();

    if (currentUser?.organizationId != null) {
      void loadFeatureFlags(currentUser.organizationId);
      setIsLoaded(true);
    }

    return cleanup;
  }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background transition-colors">
      {isLoaded &&
          <>
            {hasNewVersion && (
              <VersionCheckBanner
                oldVersion={currentVersion}
                newVersion={newVersion}/>
            )}
          </>
      }
      {/* Header */}
      <header className="flex shrink-0 justify-between items-center px-4 md:px-6 lg:px-8 py-2 border-b border-border bg-sidebar text-sidebar-foreground">
        <div className="flex items-center gap-2 md:gap-3">
          <img
            src={TendrilLogoColorTyped}
            alt="Tendril Logo"
            className="h-8 md:h-9 lg:h-10 w-auto"
            draggable={false}
          />
          <div className="flex flex-col leading-tight">
            <span className="text-white text-xs md:text-sm lg:text-base">
              {t('agentLayout.welcome')}
            </span>
            <span className="text-base md:text-lg lg:text-xl font-semibold">
              {currentUser?.userName}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {ENV !== 'prod' ? <EnvVersionBanner /> : null}
          <LanguageToggle />
          <ModeToggle className="bg-background" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <UserDropdown isOpen={false} />
        </div>
      </header>
      
      {/* Main */}
      <main
        data-qa-id="agent-main"
        className="flex flex-col flex-1 w-full bg-background text-foreground min-h-0"
      >
        <MicrophoneSetupModal />
        <div className="w-full px-3 md:px-4 lg:px-6 xl:px-10 2xl:px-16 py-1.5 md:py-2 mx-auto min-h-0 flex-1 flex flex-col overflow-hidden">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AgentLayout;
